import { useEffect, useRef, useState } from "react";

// VirtualScreen — escalador de fidelidad para los previews del admin.
//
// Renderiza sus children dentro de un lienzo de tamaño REAL fijo (REAL_W x
// REAL_H, portrait de la pantalla) y lo encoge con UN SOLO transform: scale()
// uniforme. Asi proporciones, paddings (p-6), gaps, alineaciones y tamaños en
// px quedan IDENTICOS a la pantalla real a cualquier resolución — sin los
// clamps por-medida que deformaban el preview viejo.
//
// CONTAIN (fix 2026): medimos el ANCHO **y** el ALTO disponibles del contenedor
// y escalamos por el lado que MENOS entra (scale = min(w/realW, h/realH)), igual
// que FitScreen. El marco se dimensiona EXACTO al lienzo contenido y se centra.
// Antes el marco se estiraba al 100% del ancho y, si el panel era ancho (p.ej.
// el monitor horizontal del admin), el alto del lienzo se pasaba y se RECORTABA
// abajo (overflow hidden) — por eso "Abajo" del juego se veia cortado. Ahora el
// lienzo entra completo siempre.
//
// Lienzo de referencia: el MISMO que usa la pantalla real (FitScreen). Por eso
// el preview coincide 100% con la pantalla a cualquier resolución — ambos
// renderizan este lienzo escalado uniformemente.
export const REAL_W = 1080;
export const REAL_H = 1920;

export function VirtualScreen({
  realW = REAL_W,
  realH = REAL_H,
  bgStyle,
  className,
  children,
}: {
  realW?: number;
  realH?: number;
  bgStyle?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: realW, h: realH });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr && cr.width > 0 && cr.height > 0) {
        setBox({ w: cr.width, h: cr.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // contain: el lienzo entra COMPLETO (sin recortar). Escala por el lado que
  // menos entra; el marco queda del tamaño exacto del lienzo contenido.
  const scale = Math.min(box.w / realW, box.h / realH) || 0;
  const frameW = realW * scale;
  const frameH = realH * scale;

  return (
    // El contenedor medido llena el espacio que le da el padre y centra el
    // marco contenido (igual que FitScreen centra el lienzo en la pantalla).
    <div
      ref={ref}
      className="flex h-full w-full items-center justify-center overflow-hidden"
    >
      <div
        className={`relative overflow-hidden rounded-xl border border-white/15 ${className ?? ""}`}
        style={{
          width: frameW,
          height: frameH,
          backgroundColor: "#0f0c29",
          ...bgStyle,
        }}
      >
        <div
          style={{
            width: realW,
            height: realH,
            transformOrigin: "top left",
            transform: `scale(${scale})`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
